import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { delByPathId, getDevicesTypes, getDeviceTypeById, postNewDeviceType, putDeviceType } from 'services/api';

export interface DeviceTypesState {
  list: {
    data: any[];
    status: null | 'idle' | 'loading' | 'failed';
  };
  current: {
    data: any | null;
    status: null | 'idle' | 'loading' | 'failed';
  };
  created: boolean | null;
  updated: boolean | null;
}

const initialState: DeviceTypesState = {
  list: {
    data: [],
    status: null,
  },
  current: {
    data: null,
    status: null,
  },
  created: null,
  updated: null,
};

export const fetchDeviceTypesAsync: any = createAsyncThunk(
  'deviceTypes/fetchAll',
  async () => {
    const response = await getDevicesTypes();
    return response?.data;
  },
);

export const getDeviceTypeAsync: any = createAsyncThunk(
  'deviceTypes/getById',
  async (id: string) => {
    const response = await getDeviceTypeById(id);
    return response?.data;
  },
);

export const createDeviceTypeAsync: any = createAsyncThunk(
  'deviceTypes/create',
  async (data: any) => {
    const response = await postNewDeviceType(data);
    return response?.data;
  },
);

export const updateDeviceTypeAsync: any = createAsyncThunk(
  'deviceTypes/update',
  async (data: any) => {
    const response = await putDeviceType(data);
    return response?.data;
  },
);

export const deleteDeviceTypeAsync: any = createAsyncThunk(
  'deviceTypes/delete',
  async (id: string) => {
    const response = await delByPathId('devices/types', id);
    return response?.data;
  },
);

export const deviceTypesSlice = createSlice({
  name: 'deviceTypes',
  initialState,
  reducers: {
    resetCurrent: (state) => {
      state.current.data = null;
      state.current.status = null;
    },
    resetCreated: (state) => {
      state.created = null;
    },
    resetUpdated: (state) => {
      state.updated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceTypesAsync.pending, (state) => {
        state.list.status = 'loading';
      })
      .addCase(fetchDeviceTypesAsync.fulfilled, (state, action) => {
        state.list.status = 'idle';
        state.list.data = action.payload?.items ?? [];
      })
      .addCase(fetchDeviceTypesAsync.rejected, (state) => {
        state.list.status = 'failed';
      })
      .addCase(getDeviceTypeAsync.pending, (state) => {
        state.current.status = 'loading';
      })
      .addCase(getDeviceTypeAsync.fulfilled, (state, action) => {
        state.current.status = 'idle';
        state.current.data = action.payload?.item ?? null;
      })
      .addCase(getDeviceTypeAsync.rejected, (state) => {
        state.current.status = 'failed';
      })
      .addCase(createDeviceTypeAsync.fulfilled, (state, action) => {
        state.created = action.payload?.success ?? false;
      })
      .addCase(updateDeviceTypeAsync.fulfilled, (state, action) => {
        state.updated = action.payload?.success ?? false;
      })
      .addCase(deleteDeviceTypeAsync.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (item: any) => item.id !== action.meta.arg
        );
      });
  },
});

export const { resetCurrent, resetCreated, resetUpdated } = deviceTypesSlice.actions;
export default deviceTypesSlice.reducer;
